import React, { useEffect, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { frequencyLabel } from '../../data/labels';
import { Animal, TaskTemplate } from '../../types/family';
import { uid } from '../../utils/db';
import { namesOf } from '../../utils/members';
import { Button } from '../ui/Button';
import { Chip, Field, Toggle } from '../ui/Form';
import { EmptyNote } from '../ui/Panel';
import { Sheet } from '../ui/Sheet';
import { blankTemplate, TaskEditSheet } from './TaskEditSheet';
export function AnimalsSettings() {
  const {
    family,
    saveAnimal,
    deleteAnimal
  } = useAppState();
  const [animal, setAnimal] = useState<Animal | null>(null);
  const [task, setTask] = useState<TaskTemplate | null>(null);
  if (!family.modules.zivali) return <EmptyNote data-id="element-524" data-mp="1jvvsft4gkef5">Modul Živali je izklopljen. Vklopiš ga v Opomniki in moduli.</EmptyNote>;
  return <div className="space-y-4" data-id="element-525" data-mp="1exaszvx15yrr">
      <div className="flex justify-end" data-id="element-526" data-mp="1ew9gdn0d947x">
        <Button size="sm" onClick={() => setAnimal({
        id: uid('a'),
        name: '',
        species: '',
        caretakerIds: [],
        confirmed: true
      })} data-id="element-527" data-mp="2jebuifb1g1zu">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-528" data-mp="2ndkonl9zq1vx" /> Dodaj žival
        </Button>
      </div>
      {family.animals.length === 0 && <EmptyNote data-id="element-529" data-mp="2s86pwvmcye2a">Še ni živali.</EmptyNote>}
      <ul className="space-y-3" data-id="element-530" data-mp="2x3vqn2a46106">
        {family.animals.map(a => {
        const tasks = family.templates.filter(t => t.animalIds?.includes(a.id));
        return <li key={a.id} className="rounded-3xl border-[1.5px] border-line bg-surface p-4" data-id="element-531" data-mp="24fq54db8k7za">
              <div className="flex items-start gap-3" data-id="element-532" data-mp="29fm94cjxnzeu">
                <button type="button" onClick={() => setAnimal(a)} className="min-w-0 flex-1 text-left" data-id="element-533" data-mp="29e1gwtl3csk5">
                  <span className="block font-display text-lg font-semibold hover:underline" data-id="element-534" data-mp="2eccosyrtsszq">{a.name}</span>
                  <span className="block text-xs text-muted" data-id="element-535" data-mp="3kwvskz32arg5">
                    {a.species}
                    {a.habitat ? ` · ${a.habitat}` : ''} · {a.caretakerIds.length ? `skrbi ${namesOf(family, a.caretakerIds)}` : 'sprotni dogovor'}
                    {!a.confirmed ? ' · profil ni potrjen' : ''}
                  </span>
                </button>
                <Button size="sm" variant="ghost" onClick={() => setTask({
              ...blankTemplate(`${a.name} – `, 'zivali'),
              animalIds: [a.id],
              assigneeIds: a.caretakerIds,
              confirmation: 'zaupanje',
              frequency: {
                kind: 'dnevno',
                times: 1
              },
              baseValue: 1
            })} data-id="element-536" data-mp="3a2lh8ua4wc78">
                  + opravilo
                </Button>
              </div>
              {tasks.length > 0 && <ul className="mt-2 flex flex-wrap gap-2" data-id="element-537" data-mp="2r4imdyuwnl1i">
                  {tasks.map(t => <li key={t.id} data-id="element-538" data-mp="2mec0i6rs2emy">
                      <button type="button" onClick={() => setTask(t)} className="rounded-full bg-canvas px-3 py-1.5 text-xs font-bold hover:bg-line/50" data-id="element-539" data-mp="2mdabeydxfrm8">
                        {t.title.replace(`${a.name} – `, '')} · {frequencyLabel(t.frequency)}
                      </button>
                    </li>)}
                </ul>}
            </li>;
      })}
      </ul>
      <p className="text-xs text-muted" data-id="element-540" data-mp="dyue2ubye3q9">Skupno bivališče (npr. dva polha) ima en sklop oskrbe – ista oskrba se ne podvoji.</p>
      <AnimalSheet animal={animal} onClose={() => setAnimal(null)} onSave={saveAnimal} onDelete={deleteAnimal} data-id="element-541" data-mp="3vchetlqryie4" />
      <TaskEditSheet template={task} onClose={() => setTask(null)} data-id="element-542" data-mp="45wuekv868q2" />
    </div>;
}
function AnimalSheet({
  animal,
  onClose,
  onSave,
  onDelete
}: {
  animal: Animal | null;
  onClose: () => void;
  onSave: (a: Animal) => void;
  onDelete: (id: string) => void;
}) {
  const {
    family,
    activeMembers
  } = useAppState();
  const [a, setA] = useState<Animal | null>(animal);
  useEffect(() => setA(animal), [animal]);
  if (!a) return <Sheet open={false} onClose={onClose} title="" children={null} data-id="element-543" data-mp="33nmlhsbga3a1" />;
  const set = (p: Partial<Animal>) => setA(cur => cur ? {
    ...cur,
    ...p
  } : cur);
  const exists = family.animals.some(x => x.id === a.id);
  return <Sheet open onClose={onClose} title={a.name || 'Nova žival'} footer={<div className="flex gap-2" data-id="element-545" data-mp="3j4ctvqzt2zgx">
          {exists && <Button variant="secondary" aria-label="Odstrani žival" onClick={() => {
      onDelete(a.id);
      onClose();
    }} data-id="element-546" data-mp="3e6b30jw51vuw">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-547" data-mp="397q9f3be6aff" />
            </Button>}
          <Button className="flex-1" disabled={!a.name.trim()} onClick={() => {
      onSave({
        ...a,
        name: a.name.trim()
      });
      onClose();
    }} data-id="element-548" data-mp="aq48b2vpb8y9">
            Shrani
          </Button>
        </div>} data-id="element-544" data-mp="3nx5ymev9y7t0">
      <div className="space-y-4" data-id="element-549" data-mp="qvh2hs808fl">
        <div className="grid grid-cols-2 gap-3" data-id="element-550" data-mp="ptubg1udxi1">
          <Field label="Ime" data-id="element-551" data-mp="3svh98rte8mca">{id => <input id={id} className="field" value={a.name} onChange={e => set({
            name: e.target.value
          })} data-id="element-552" data-mp="3svz53nc58yjo" />}</Field>
          <Field label="Vrsta" data-id="element-553" data-mp="2lif5xe2nq1xl">{id => <input id={id} className="field" value={a.species} onChange={e => set({
            species: e.target.value
          })} placeholder="npr. mačka" data-id="element-554" data-mp="2lfj8dquq1f9o" />}</Field>
        </div>
        <Field label="Bivališče (skupno, če si ga deli več živali)" data-id="element-555" data-mp="wz1bzddz161j">{id => <input id={id} className="field" value={a.habitat ?? ''} onChange={e => set({
          habitat: e.target.value || undefined
        })} data-id="element-556" data-mp="10zbmn3cpkkmq" />}</Field>
        <div data-id="element-557" data-mp="15xwhen96yc3s">
          <p className="mb-1.5 text-sm font-bold" data-id="element-558" data-mp="15xd97y9cf22y">Skrbniki (prazno = sprotni dogovor)</p>
          <div className="flex flex-wrap gap-2" data-id="element-559" data-mp="1aqz90puerbhc">
            {activeMembers.map(m => <Chip key={m.id} color={m.color} active={a.caretakerIds.includes(m.id)} onClick={() => set({
            caretakerIds: a.caretakerIds.includes(m.id) ? a.caretakerIds.filter(x => x !== m.id) : [...a.caretakerIds, m.id]
          })} data-id="element-560" data-mp="1fnpszo9c2r81">
                {m.name}
              </Chip>)}
          </div>
        </div>
        <Toggle checked={a.confirmed} onChange={v => set({
        confirmed: v
      })} label="Potrjen profil" hint="Nepotrjene povezave (domneve) so jasno označene." data-id="element-561" data-mp="1tnh9pwr8ilpq" />
      </div>
    </Sheet>;
}